import { useId, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import {
  getExternalRegisterAlertTypeLabel,
} from "@/components/externalregister/helpers";
import { useTypes } from "@/providers/TypesProvider";
import { optionsSex } from "@/TrierStandards";

import type {
  ExternalRegisterEntryRecord,
} from "./externalRegisterAdminTypes";
import { EXTERNAL_REGISTER_ADMIN_ALERT_TYPES } from "./externalRegisterAdminTypes";
import {
  compactFieldSx,
  compactSelectSx,
  fieldGridSx,
  fieldLabel,
  multilineFieldSx,
} from "./externalRegisterAdminUi";
import { Icon } from "@/components/Icon";

import {
  EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS,
  getEntryRecordValidationErrors,
  normalizeExternalRegisterAliases,
  parseExternalRegisterAliases,
} from "./externalRegisterAdminHelpers";
import { useTranslation } from "react-i18next";

const noAutocompleteInputProps = (formId: string, fieldIndex: number, ariaLabel: string, maxLength?: number) => ({
  "aria-label": ariaLabel,
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
  name: `er-entry-${formId}-${fieldIndex}`,
  id: `er-entry-field-${formId}-${fieldIndex}`,
  "data-lpignore": "true",
  "data-form-type": "other",
  ...(maxLength ? { maxLength } : {}),
});

export const EntryForm = ({
  value,
  onChange,
  showValidation = false,
}: {
  value: ExternalRegisterEntryRecord;
  onChange: (value: ExternalRegisterEntryRecord) => void;
  showValidation?: boolean;
}) => {
  const { t } = useTranslation();
  const { sexOptions } = useTypes();
  const formId = useId().replace(/[^a-z0-9-]/gi, "-");
  const [touched, setTouched] = useState<Partial<Record<keyof ExternalRegisterEntryRecord, boolean>>>({});
  const errors = getEntryRecordValidationErrors(value);
  const configuredSexOptions = (sexOptions.length > 0 ? sexOptions : optionsSex)
    .map((option) => ({
      value: option.value,
      label: option.label,
      disabled: "disabled" in option && option.disabled === true,
    }));
  const hasCurrentUnknownSex = Boolean(
    value.sex
    && !configuredSexOptions.some((option) => option.value === value.sex),
  );
  const [pendingAliasText, setPendingAliasText] = useState("");
  const pendingAliases = parseExternalRegisterAliases(pendingAliasText);
  const getError = (field: keyof ExternalRegisterEntryRecord) => (
    showValidation || touched[field] ? errors[field] : undefined
  );
  const markTouched = (field: keyof ExternalRegisterEntryRecord) => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const update = (field: keyof ExternalRegisterEntryRecord, nextValue: string) => {
    markTouched(field);
    onChange({ ...value, [field]: nextValue });
  };

  const addPendingAliases = () => {
    markTouched("aliases");
    if (pendingAliases.length === 0) return;
    onChange({ ...value, aliases: normalizeExternalRegisterAliases([...value.aliases, ...pendingAliases]) });
    setPendingAliasText("");
  };

  const removeAlias = (aliasToRemove: string) => {
    markTouched("aliases");
    onChange({ ...value, aliases: value.aliases.filter((alias) => alias !== aliasToRemove) });
  };

  return (
    <Box component="form" autoComplete="off" onSubmit={(event) => event.preventDefault()} sx={fieldGridSx}>
      <TextField size="small" autoComplete="off" label={fieldLabel(t("externalRegister.admin.entryForm.fields.surname.label"), t("externalRegister.admin.entryForm.fields.surname.tooltip"))} value={value.surname} onChange={(event) => update("surname", event.target.value)} onBlur={() => markTouched("surname")} required error={Boolean(getError("surname"))} helperText={getError("surname")} inputProps={noAutocompleteInputProps(formId, 1, t("externalRegister.admin.entryForm.fields.surname.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.surname)} sx={compactFieldSx} />
      <TextField size="small" autoComplete="off" label={fieldLabel(t("externalRegister.admin.entryForm.fields.givenName.label"), t("externalRegister.admin.entryForm.fields.givenName.tooltip"))} value={value.givenName} onChange={(event) => update("givenName", event.target.value)} onBlur={() => markTouched("givenName")} required error={Boolean(getError("givenName"))} helperText={getError("givenName")} inputProps={noAutocompleteInputProps(formId, 2, t("externalRegister.admin.entryForm.fields.givenName.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.givenName)} sx={compactFieldSx} />
      <FormControl size="small" required error={Boolean(getError("type"))} sx={compactSelectSx}>
        <InputLabel id="external-register-entry-type-label">{fieldLabel(t("externalRegister.admin.entryForm.fields.alertType.label"), t("externalRegister.admin.entryForm.fields.alertType.tooltip"))}</InputLabel>
        <Select
          labelId="external-register-entry-type-label"
          label={t("externalRegister.admin.entryForm.fields.alertType.label")}
          value={value.type}
          onChange={(event) => update("type", event.target.value)}
          onBlur={() => markTouched("type")}
          inputProps={{
            autoComplete: "off",
            name: `er-entry-${formId}-3`,
            id: `er-entry-field-${formId}-3`,
          }}
        >
          {EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {getExternalRegisterAlertTypeLabel(type)}
            </MenuItem>
          ))}
        </Select>
        {getError("type") ? <FormHelperText>{getError("type")}</FormHelperText> : null}
      </FormControl>
      <TextField size="small" autoComplete="off" label={fieldLabel(t("externalRegister.admin.entryForm.fields.hospital.label"), t("externalRegister.admin.entryForm.fields.hospital.tooltip"))} value={value.hospital} onChange={(event) => update("hospital", event.target.value)} onBlur={() => markTouched("hospital")} required error={Boolean(getError("hospital"))} helperText={getError("hospital")} inputProps={noAutocompleteInputProps(formId, 4, t("externalRegister.admin.entryForm.fields.hospital.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.hospital)} sx={compactFieldSx} />
      <TextField size="small" autoComplete="off" type="date" label={fieldLabel(t("externalRegister.admin.entryForm.fields.dateListed.label"), t("externalRegister.admin.entryForm.fields.dateListed.tooltip"))} value={value.dateListed} onChange={(event) => update("dateListed", event.target.value)} onBlur={() => markTouched("dateListed")} required error={Boolean(getError("dateListed"))} helperText={getError("dateListed")} inputProps={noAutocompleteInputProps(formId, 5, t("externalRegister.admin.entryForm.fields.dateListed.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.dateListed)} InputLabelProps={{ shrink: true }} sx={compactFieldSx} />
      <Box sx={multilineFieldSx}>
        <Stack direction="row" gap={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            label={fieldLabel(t("externalRegister.admin.entryForm.fields.aliases.label", { defaultValue: "Add alias" }), t("externalRegister.admin.entryForm.fields.aliases.tooltip", { defaultValue: "Type one or more aliases separated by comma or pipe, then add them to the entry." }))}
            value={pendingAliasText}
            onChange={(event) => setPendingAliasText(event.target.value)}
            onBlur={() => markTouched("aliases")}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addPendingAliases();
              }
            }}
            error={Boolean(getError("aliases"))}
            helperText={getError("aliases")}
            inputProps={noAutocompleteInputProps(formId, 6, t("externalRegister.admin.entryForm.fields.aliases.ariaLabel", { defaultValue: "Entry aliases" }))}
            sx={compactFieldSx}
          />
          <IconButton aria-label={t("externalRegister.admin.entryForm.fields.aliases.addButtonAriaLabel", { defaultValue: "Add aliases" })} disabled={pendingAliases.length === 0} onClick={addPendingAliases}>
            <Icon icon="add" />
          </IconButton>
        </Stack>
        {value.aliases.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            {value.aliases.map((alias) => (
              <Chip key={alias} size="small" label={alias} onDelete={() => removeAlias(alias)} sx={{ borderRadius: 1, maxWidth: "100%" }} />
            ))}
          </Stack>
        ) : null}
      </Box>
      <FormControl size="small" error={Boolean(getError("sex"))} sx={compactSelectSx}>
        <InputLabel id="external-register-entry-sex-label">{fieldLabel(t("externalRegister.admin.entryForm.fields.sex.label"), t("externalRegister.admin.entryForm.fields.sex.tooltip"))}</InputLabel>
        <Select
          labelId="external-register-entry-sex-label"
          label={t("externalRegister.admin.entryForm.fields.sex.label")}
          value={value.sex ?? ""}
          onChange={(event) => update("sex", event.target.value)}
          onBlur={() => markTouched("sex")}
          inputProps={{
            "aria-label": t("externalRegister.admin.entryForm.fields.sex.ariaLabel"),
            autoComplete: "off",
            name: `er-entry-${formId}-7`,
            id: `er-entry-field-${formId}-7`,
          }}
        >
          <MenuItem value="">
            {t("common.empty.notRecorded")}
          </MenuItem>
          {configuredSexOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </MenuItem>
          ))}
          {hasCurrentUnknownSex ? (
            <MenuItem value={value.sex} disabled>
              {t("externalRegister.admin.entryForm.fields.sex.currentValue", { value: value.sex })}
            </MenuItem>
          ) : null}
        </Select>
        {getError("sex") ? <FormHelperText>{getError("sex")}</FormHelperText> : null}
      </FormControl>
      <TextField size="small" autoComplete="off" type="date" label={fieldLabel(t("externalRegister.admin.entryForm.fields.dob.label"), t("externalRegister.admin.entryForm.fields.dob.tooltip"))} value={value.dob ?? ""} onChange={(event) => update("dob", event.target.value)} onBlur={() => markTouched("dob")} error={Boolean(getError("dob"))} helperText={getError("dob")} inputProps={noAutocompleteInputProps(formId, 8, t("externalRegister.admin.entryForm.fields.dob.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.dob)} InputLabelProps={{ shrink: true }} sx={compactFieldSx} />
      <TextField size="small" autoComplete="off" label={fieldLabel(t("externalRegister.admin.entryForm.fields.umrn.label"), t("externalRegister.admin.entryForm.fields.umrn.tooltip"))} value={value.umrn ?? ""} onChange={(event) => update("umrn", event.target.value)} onBlur={() => markTouched("umrn")} error={Boolean(getError("umrn"))} helperText={getError("umrn")} inputProps={noAutocompleteInputProps(formId, 9, t("externalRegister.admin.entryForm.fields.umrn.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.umrn)} sx={compactFieldSx} />
      <TextField
        size="small"
        autoComplete="off"
        label={fieldLabel(t("externalRegister.admin.entryForm.fields.comments.label"), t("externalRegister.admin.entryForm.fields.comments.tooltip"))}
        value={value.comments ?? ""}
        onChange={(event) => update("comments", event.target.value)}
        onBlur={() => markTouched("comments")}
        error={Boolean(getError("comments"))}
        helperText={getError("comments")}
        multiline
        minRows={3}
        sx={multilineFieldSx}
        inputProps={noAutocompleteInputProps(formId, 10, t("externalRegister.admin.entryForm.fields.comments.ariaLabel"), EXTERNAL_REGISTER_ENTRY_FIELD_LIMITS.comments)}
      />
    </Box>
  );
};
